import { useCallback } from 'react';
import classNames from './DeleteStudentsPopup.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentReportItem } from '@/school-management/types/response';
import { formatPersonName } from '@/utils/personName';
import {
  BulkOperationResponseData,
  DeleteStudentsBulkOperationItem,
  BulkOperationState,
} from '@/school-management/types/services/bulk';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import processing from '@/school-management/utils/processing';

interface DownloadProps {
  students: StudentReportItem[];
  bulkOperation?: BulkOperationResponseData;
  bulkOperationState:
    | BulkOperationState.Completed
    | BulkOperationState.PartialSuccess
    | BulkOperationState.Failed;
}

const Download = ({ students, bulkOperation, bulkOperationState }: DownloadProps) => {
  const { t } = useAdminTranslation();

  const download = useCallback(() => {
    const studentsToProcess = students || [];

    const bulkStudentsMapper = (
      (bulkOperation?.results || []) as DeleteStudentsBulkOperationItem[]
    ).reduce(
      (acc, s) => {
        if (s.studentId) {
          acc[s.studentId] = s;
        }

        return acc;
      },
      {} as {
        [studentId: string]: DeleteStudentsBulkOperationItem;
      }
    );

    const tableName = t('bulkActions.deleteStudents.report.fileName');
    const keys = {
      name: t('bulkActions.common.name'),
      teacher: t('bulkActions.common.teacher'),
      class: t('bulkActions.deleteStudents.report.class'),
      grade: t('grade'),
      action: t('bulkActions.common.action'),
      status: t('status'),
    };
    const headerRow: string[] = [
      keys.name,
      keys.teacher,
      keys.class,
      keys.grade,
      keys.action,
      keys.status,
    ];
    const data: {
      [key: string]: any;
    }[] = studentsToProcess.map(s => {
      const isSuccess =
        bulkOperationState === BulkOperationState.Completed
          ? true
          : bulkOperationState === BulkOperationState.Failed
            ? false
            : s.userId in bulkStudentsMapper
              ? bulkStudentsMapper[s.userId].success
              : true;
      const error =
        bulkOperationState === BulkOperationState.Completed
          ? ''
          : s.userId in bulkStudentsMapper
            ? bulkStudentsMapper[s.userId].error
            : bulkOperationState === BulkOperationState.Failed
              ? 'Unexpected error'
              : '';

      return {
        [keys.name]: s.name,
        [keys.teacher]: (s.teachers || [])
          .map(t => formatPersonName(t.firstName, t.lastName))
          .join(', '),
        [keys.class]: s.groupName,
        [keys.grade]: `${t(`grade_${s.gradeId}`)} ${t('grade')}`,
        [keys.action]: 'Delete',
        [keys.status]: isSuccess ? 'success' : 'failure',
      };
    });

    processing.createXlsx({
      fileName: tableName,
      tables: [
        {
          name: tableName,
          headerRow: headerRow,
          data: data,
        },
      ],
    });
  }, [students, bulkOperation, bulkOperationState, t]);

  return (
    <div className={classNames.download}>
      <button onClick={download}>
        <AppIcon icon='download' width={10} height={15} />
        <span>{t('bulkActions.downloadChanges.downloadButton')}</span>
      </button>
      <div>{t('bulkActions.downloadChanges.text')}</div>
    </div>
  );
};

export default Download;
